import AnyCard from 'components/Card'
import LoadingCard from 'components/Card/LoadingCard'
import CardList, { CARD_LIST_HEIGHT_DEFAULT, ITEM_WIDTH_DEFAULT } from 'components/CardList'
import { AnyContent, STATUS_TYPE, Stream } from 'interfaces'
import { memo, ReactElement, useCallback, useMemo } from 'react'
import styles from './CuratedFolderListView.pcss'

const LOADING_CARDS_COUNT = 16

export interface CuratedFolderListViewProps {
  stream: Stream
  loading: boolean
  items: AnyContent[]

  onScrolled?(stream: Stream, direction: 'left' | 'right'): void
  onItemPinned?(stream: Stream, content: AnyContent): void
  onItemShared?(stream: Stream, content: AnyContent): void
  onGotoClicked?(stream: Stream, content: AnyContent, by: string): void

  onScrollLimit?(): void
}

export function CuratedFolderListView(props: CuratedFolderListViewProps) {
  const { stream, loading, items, onScrolled, onItemShared, onItemPinned, onGotoClicked, onScrollLimit } = props

  const handleScrolled = useCallback((direction: 'left' | 'right') => {
    if (onScrolled) {
      onScrolled(stream, direction)
    }
  }, [stream, onScrolled])

  const handleItemShared = useCallback((content: AnyContent) => {
    if (onItemShared) {
      onItemShared(stream, content)
    }
  }, [stream, onItemShared])

  const handleItemPinned = useCallback((content: AnyContent) => {
    if (onItemPinned) {
      onItemPinned(stream, content)
    }
  }, [stream, onItemPinned])

  const handleGotoClicked = useCallback((content: AnyContent, by: string) => {
    if (onGotoClicked) {
      onGotoClicked(stream, content, by)
    }
  }, [stream, onGotoClicked])

  const handleFeedClicked = useCallback((content: AnyContent) => {
    handleGotoClicked(content, 'attribution')
  }, [handleGotoClicked])

  const renderLoading = useCallback((): (ReactElement<any> | number | string)[] => {
    return new Array(LOADING_CARDS_COUNT)
      .fill('')
      .map((_, index) => (
        <LoadingCard key={index} square small={true} active={loading} />
      ))
  }, [loading])

  const renderListItem = useCallback((item: AnyContent): ReactElement<any> | number | string => {
    return (
      <AnyCard
        key={item.id}
        children={item}
        content={item as any}
        square
        small={true}
        onCompose={handleItemShared}
        {...(item.type === STATUS_TYPE
          ? {
            className: styles['status-card']
          }
          : {
            onPin: handleItemPinned,
            onSourceClick: handleGotoClicked,
            onFeedClick: handleFeedClicked
          }
        ) as any}
      />
    )
  }, [handleItemShared, handleItemPinned, handleGotoClicked, handleFeedClicked])

  const listContent = useMemo(() => {
    const itemsToRender = loading || items.length === 0
      ? items.map(renderListItem).concat(renderLoading())
      : items.map(renderListItem)

    return itemsToRender
  }, [loading, items, renderListItem, renderLoading])

  return (
    <CardList
      key="content"
      itemWidth={ITEM_WIDTH_DEFAULT}
      listHeight={CARD_LIST_HEIGHT_DEFAULT}
      containerClassName={styles.list}
      onScrolled={handleScrolled}
      onScrollLimit={onScrollLimit}
    >
      {listContent}
    </CardList>
  )
}

export default memo(CuratedFolderListView)
